import { queryClient } from "App";
import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { get } from "lodash";
import { toast } from "sonner";

import { formatError } from "helpers/formatError";
import {
  getCustomFieldConfigTableColumns,
  getCustomFieldConfigTableFilters,
} from "routes/custom_field/components/custom_field_config_table/CustomFieldConfigTableColumnsAndFilters";
import { CUSTOM_FIELD_CONFIG_TABLE_CONSTANTS } from "routes/custom_field/components/custom_field_config_table/CustomFieldConfigTableV3";
import CustomFieldService from "services/CustomField";
import UserService from "services/User";
import { addResizedColumns } from "hooks/useTableConfig";

export const invalidateCustomFieldConfigsQuery = async () => {
  await queryClient.invalidateQueries({ queryKey: ["model-type-options"] });
  await queryClient.invalidateQueries({
    queryKey: [CUSTOM_FIELD_CONFIG_TABLE_CONSTANTS.queryKey],
  });
  await queryClient.invalidateQueries({ queryKey: ["custom-fields"] });
  await queryClient.invalidateQueries({ queryKey: ["custom-field-config"] });
  // The Custom Field Group form reads a model's fields from this query, so a
  // new or renamed config has to drop out of its cache too.
  await queryClient.invalidateQueries({
    queryKey: ["custom-field-configs-for-model"],
  });
};

export const getModelTypes = async () => {
  const modelTypes = await CustomFieldService.getCustomFieldConfigModelTypes();
  const modelTypeOptions = modelTypes.map(modelType => ({
    id: modelType,
    name: modelType,
  }));
  return modelTypeOptions;
};

export const useCustomFieldConfigMutations = ({ onDeleted } = {}) => {
  const create = useMutation({
    mutationFn: data => CustomFieldService.createCustomFieldConfig(data),
    onSuccess: () => invalidateCustomFieldConfigsQuery(),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) =>
      CustomFieldService.updateCustomFieldConfig(id, data),
    onSuccess: () => invalidateCustomFieldConfigsQuery(),
  });

  const remove = useMutation({
    mutationFn: id => CustomFieldService.deleteCustomFieldConfig(id),
    onSuccess: () => {
      invalidateCustomFieldConfigsQuery();
      toast.success("Custom field config deleted");
      onDeleted?.();
    },
    onError: err =>
      toast.error(formatError(err, "Failed to delete custom field config")),
  });

  return { create, update, remove };
};

export const useCustomFieldConfigTable = () => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });
  const { data: modelTypeOptions, isSuccess: isModelTypeOptionsFetched } =
    useQuery({
      queryKey: [
        "model-type-options",
        CUSTOM_FIELD_CONFIG_TABLE_CONSTANTS.tableName,
      ],
      queryFn: getModelTypes,
    });

  const filters = useMemo(() => {
    if (!isModelTypeOptionsFetched) return [];
    return getCustomFieldConfigTableFilters({
      modelTypeOptions: modelTypeOptions || [],
    });
  }, [modelTypeOptions, isModelTypeOptionsFetched]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getCustomFieldConfigTableColumns();
    const customFieldConfigTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${CUSTOM_FIELD_CONFIG_TABLE_CONSTANTS.tableName}`,
      {}
    );

    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: customFieldConfigTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [tableSettings, isTableSettingsFetched]);

  return {
    filters,
    columns,
  };
};
